/**
 * "Rough handwriting": a deterministic per-glyph wobble applied at draw time,
 * so any font can read as hand-written. Purely visual — glyph advances are
 * untouched, so wrapping, measurement and bounding boxes are unaffected.
 */

export type RoughTextJitter = {
  /** px offset along x */
  dx: number;
  /** px offset along y (baseline variance — the real handwriting tell) */
  dy: number;
  /** radians */
  rotate: number;
};

/** roughness 0/1/2 (architect/artist/cartoonist) → wobble multiplier */
const ROUGHNESS_SCALE = [0.55, 1, 1.7];

/** stateless hash → [0, 1); same char index always wobbles the same way */
const hash = (seed: number, index: number, salt: number) => {
  let h = (seed ^ (index * 0x9e3779b1) ^ (salt * 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x297a2d39) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 0x100000000;
};

const signed = (seed: number, index: number, salt: number) =>
  hash(seed, index, salt) * 2 - 1;

export const getRoughTextJitter = (
  seed: number,
  index: number,
  fontSize: number,
  roughness: number,
): RoughTextJitter => {
  const scale = ROUGHNESS_SCALE[roughness] ?? 1;

  return {
    dx: signed(seed, index, 1) * fontSize * 0.022 * scale,
    dy: signed(seed, index, 2) * fontSize * 0.045 * scale,
    rotate: signed(seed, index, 3) * 0.026 * scale,
  };
};
